import type { TFunction } from 'i18next'
import { FilePenLine, Trash2 } from 'lucide-react'

import { UserDisplay } from '@/components'
import { ClickableCellWithTooltip } from '@/components/ClickableCellWithTooltip'
import { TableColumn } from '@/components/Table'
import { Alignment, FreezePosition } from '@/components/Table/tableHelper'
import { Button } from '@/components/ui/button'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { ProjectType } from '@/schema'
import { formatDisplayDate } from '@/utils/common'

import { ReportingEventsCell } from './components/ReportingEventsCell'
import { StatusBadge } from './components/StatusBadge'
import { Project } from './types'
import { getProjectTypeLabel } from './utils'

export type ProjectAction = 'edit' | 'delete' | 'view-detail'

export const getProjectColumns = (
  handleActionClick: (action: ProjectAction, item: Project) => void,
  t: TFunction
): TableColumn<Project>[] => [
  {
    id: 'project_name',
    label: t('project.columns.name'),
    width: 240,
    align: Alignment.LEFT,
    sortable: true,
    alignLabel: Alignment.LEFT,
    renderCell: (value, row) => (
      <ClickableCellWithTooltip
        value={typeof value === 'string' ? (value?.toString() ?? '-') : '-'}
        onClick={() => handleActionClick('view-detail', row)}
      />
    ),
    isFrozen: true,
    freezePosition: FreezePosition.LEFT,
  },
  {
    id: 'project_type',
    label: t('project.columns.projectType'),
    width: 200,
    renderCell: (value) => (
      <span>{getProjectTypeLabel(value as ProjectType)}</span>
    ),
  },
  {
    id: 'company_name',
    label: t('project.columns.company'),
    width: 200,
    sortable: true,
    align: Alignment.LEFT,
    alignLabel: Alignment.LEFT,
    renderCell: (_value, row) => {
      return <span>{row?.company_name || '-'}</span>
    },
  },
  {
    id: 'reporting_events',
    label: t('project.columns.reportingEvents'),
    width: 240,
    align: Alignment.LEFT,
    alignLabel: Alignment.LEFT,
    renderCell: (value) => {
      const events = Array.isArray(value) ? value : []
      return <ReportingEventsCell events={events} />
    },
  },
  {
    id: 'total_draft',
    label: t('project.columns.draftDeliverableSets'),
    width: 225,
    sortable: true,
    align: Alignment.RIGHT,
  },
  {
    id: 'total_open_comments_count',
    label: t('project.columns.openComments'),
    width: 176,
    sortable: true,
    align: Alignment.RIGHT,
    renderCell: (value) => <span>{Number(value) || 0}</span>,
  },
  {
    id: 'status',
    label: t('project.columns.status'),
    width: 123,
    sortable: true,
    align: Alignment.RIGHT,
    alignLabel: Alignment.RIGHT,
    renderCell: (value) =>
      value ? (
        <StatusBadge
          status={typeof value === 'string' ? (value?.toString() ?? '-') : '-'}
        />
      ) : (
        <span>-</span>
      ),
  },
  {
    id: 'created_by_user',
    label: t('project.columns.createdBy'),
    width: 170,
    sortable: true,
    align: Alignment.LEFT,
    alignLabel: Alignment.LEFT,
    renderCell: (value) => {
      const user =
        value && typeof value === 'object' && 'first_name' in value
          ? value
          : null
      return <UserDisplay user={user} showAvatar={false} />
    },
  },
  {
    id: 'created_at',
    label: t('project.columns.createdDate'),
    width: 150,
    sortable: true,
    align: Alignment.LEFT,
    alignLabel: Alignment.LEFT,
    renderCell: (value) => <span>{formatDisplayDate(value as string)}</span>,
  },
  {
    id: 'actions',
    width: 90,
    isFrozen: true,
    freezePosition: FreezePosition.RIGHT,
    renderCell: (_value, row) => (
      <div className="flex gap-0.5 items-center">
        <Tooltip delayDuration={0}>
          <TooltipTrigger asChild>
            <Button
              variant="text"
              className="border-none bg-transparent p-1.5 w-fit h-fit text-foreground-fg-quaternary-400 hover:bg-transparent"
              onClick={(e) => {
                e.stopPropagation()
                handleActionClick('edit', row)
              }}
            >
              <FilePenLine size={16} />
            </Button>
          </TooltipTrigger>
          <TooltipContent>
            <span>{t('project.actions.edit')}</span>
          </TooltipContent>
        </Tooltip>
        {row.can_delete && (
          <Tooltip delayDuration={0}>
            <TooltipTrigger asChild>
              <Button
                variant="text"
                className="border-none bg-transparent p-1.5 w-fit h-fit text-foreground-fg-quaternary-400 hover:bg-transparent"
                onClick={(e) => {
                  e.stopPropagation()
                  handleActionClick('delete', row)
                }}
              >
                <Trash2 size={16} />
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              <span>{t('project.actions.delete')}</span>
            </TooltipContent>
          </Tooltip>
        )}
      </div>
    ),
  },
]
